import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectPupCatalog } from '../../redux/pupCatalogSlice';
import { selectActionLogHands } from '../../redux/actionLogSlice';
import { formatLogEvent } from '../../redux/actionLogFormatter';
import LogLine from './LogLine';
import PupCardDetail from './PupCardDetail';
import './ActionLog.css';

// Per-table live action log — a right-edge slide-in drawer (slide from SideMenu, scrolling list +
// auto-scroll from ChatWindow), mounted inside GameTable so split-screen gets one per table. It
// renders the RAW events of the last few retained hands through the shared pure formatter, resolving
// names from live game state and Pup names from the catalog. Private events reach here only for the
// viewing seat (the server filters them); the formatter also re-checks ownership, so another seat's
// cards never show.

const EMPTY_HANDS = [];

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'action', label: 'Actions' },
  { value: 'pup', label: 'Pup plays' },
];

// Reduce-motion: jump instead of animating the scroll (checked at scroll time, not memoized).
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

const ActionLog = ({ gameId }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [detailCode, setDetailCode] = useState(null);

  const hands = useSelector((s) => selectActionLogHands(s, gameId)) || EMPTY_HANDS;
  const players = useSelector((s) => s.games.activeGames[gameId]?.players);
  const catalog = useSelector(selectPupCatalog);
  const mySeat = useSelector((s) => s.games.mySeats[gameId]);

  // Injected resolvers keep the formatter pure and Redux-free (shared with the replay viewer).
  const ctx = useMemo(() => ({
    resolveSeatName: (seat) => players?.[seat]?.username || `Seat ${seat}`,
    resolvePupName: (code) => catalog?.[code]?.name || code,
    viewerSeat: mySeat ?? null,
  }), [players, catalog, mySeat]);

  // Flatten the retained hands (oldest→newest) into renderable lines. The formatter's per-line `key`
  // is `seq`-based and RESETS each hand, so keys collide across hands — namespace every line key with
  // its hand identity (handStartTs, or 'pending' for the single transient provisional bucket). The
  // formatter already emits a "New hand" divider per HAND_START, so hands separate visually for free.
  // Display-only filtering: structural rows (dividers, blinds/button, pot award) always show so the
  // hand skeleton stays readable; switching the filter just re-runs this memo over the same hands.
  const lines = useMemo(() => {
    const out = [];
    hands.forEach((hand) => {
      const handKey = hand.handStartTs != null ? String(hand.handStartTs) : 'pending';
      for (const event of hand.events) {
        const formatted = formatLogEvent(event, ctx);
        if (formatted == null) continue;
        (Array.isArray(formatted) ? formatted : [formatted]).forEach((line) => {
          if (filter === 'all' || line.category === 'structural' || line.category === filter) {
            out.push({ key: `${handKey}:${line.key}`, line });
          }
        });
      }
    });
    return out;
  }, [hands, ctx, filter]);

  // Auto-scroll to newest whenever the visible lines change (only while open).
  const bottomRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'end' });
  }, [lines, open]);

  return (
    <>
      {!open && (
        <button type="button" className="action-log-toggle" onClick={() => setOpen(true)} aria-label="Open action log">
          <span className="action-log-toggle-label">Log</span>
        </button>
      )}

      <aside className={`action-log ${open ? 'open' : ''}`} aria-hidden={!open}>
        <div className="action-log-header">
          <span className="action-log-title">Action Log</span>
          <select
            className="action-log-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter action log"
          >
            {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <button type="button" className="action-log-close" onClick={() => setOpen(false)} aria-label="Close action log">
            &times;
          </button>
        </div>

        <div className="action-log-list">
          {lines.length === 0 && <div className="action-log-empty">No entries to show.</div>}
          {lines.map(({ key, line }) => (
            <LogLine key={key} line={line} onPupClick={setDetailCode} roomId={gameId} />
          ))}
          <div ref={bottomRef} />
        </div>
      </aside>

      {detailCode && <PupCardDetail code={detailCode} roomId={gameId} onClose={() => setDetailCode(null)} />}
    </>
  );
};

export default ActionLog;
